import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "src/lib/utils";
import { type NavItem } from "./navConfig";
import { useSocialProfile } from "src/hooks";
import usePermissions from "src/hooks/usePermissions/usePermissions";

function useNavPermissions() {
	const { hasPermission } = usePermissions();
	const { data: profile } = useSocialProfile();

	const canShow = (item: NavItem): boolean => {
		const { guard } = item;

		if (!guard) return true;

		if (guard.requiredPermission) {
			return hasPermission(guard.requiredPermission);
		}

		if (guard.restrictedToEstateManager) {
			return (
				!!profile?.retail_user?.manager_of_area ||
				!!profile?.retail_user?.manager_of_region ||
				!!profile?.retail_user?.manager_of_store ||
				hasPermission("CanViewObservationReporting")
			);
		}

		return true;
	};

	return { canShow };
}

function filterNavItems(
	items: NavItem[],
	canShow: (item: NavItem) => boolean
): NavItem[] {
	return items.reduce<NavItem[]>((acc, item) => {
		if (item.children) {
			const visibleChildren = filterNavItems(item.children, canShow);
			if (visibleChildren.length === 0) return acc;
			if (!canShow(item)) return acc;
			acc.push({ ...item, children: visibleChildren });
		} else {
			if (canShow(item)) acc.push(item);
		}
		return acc;
	}, []);
}

interface NavItemsProps {
	items: NavItem[];
	onNavigate?: () => void;
}

interface NavItemRowProps {
	item: NavItem;
	onNavigate?: () => void;
}

function NavItemRow({ item, onNavigate }: NavItemRowProps) {
	const location = useLocation();
	const [expanded, setExpanded] = useState(() => {
		if (!item.children) return false;
		return item.children.some((child) =>
			child.path
				? location.pathname.startsWith(child.path.split("?")[0])
				: false
		);
	});

	const Icon = item.icon;

	if (item.children) {
		return (
			<div>
				<button
					onClick={() => setExpanded(!expanded)}
					aria-expanded={expanded}
					className={cn(
						"group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-body transition-all duration-150",
						"hover:bg-link/15 hover:text-link active:scale-[0.98]",
						expanded && "bg-accent"
					)}
				>
					<Icon
						className="h-5 w-5 shrink-0 text-muted-foreground transition-colors duration-150 group-hover:text-link"
						aria-hidden="true"
					/>
					<span className="flex-1 text-left">{item.label}</span>
					<ChevronDown
						className={cn(
							"h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-250 ease-out group-hover:text-link",
							expanded && "rotate-180"
						)}
						aria-hidden="true"
					/>
				</button>
				<div
					className={cn(
						"ml-8 space-y-0.5 border-l border-border pl-3 overflow-hidden transition-all duration-250 ease-out",
						expanded ? "mt-1 max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
					)}
					{...({ inert: !expanded || undefined } as { inert?: boolean })}
				>
					{item.children.map((child) => (
						<NavItemRow
							key={child.label}
							item={child}
							onNavigate={onNavigate}
						/>
					))}
				</div>
			</div>
		);
	}

	return (
		<NavLink
			to={item.path!}
			end={item.end}
			onClick={onNavigate}
			className={({ isActive }) =>
				cn(
					"group flex items-center gap-3 rounded-lg px-3 py-2 text-body transition-all duration-150 active:scale-[0.98]",
					isActive ? "bg-link text-white" : "hover:bg-link/15 hover:text-link"
				)
			}
		>
			{({ isActive }) => (
				<>
					<Icon
						aria-hidden="true"
						className={cn(
							"h-5 w-5 shrink-0 transition-colors duration-150",
							isActive
								? "text-white"
								: "text-muted-foreground group-hover:text-link"
						)}
					/>
					<span>{item.label}</span>
				</>
			)}
		</NavLink>
	);
}

export function NavItems({ items, onNavigate }: NavItemsProps) {
	const { canShow } = useNavPermissions();
	const visibleItems = filterNavItems(items, canShow);

	return (
		<nav className="space-y-1">
			{visibleItems.map((item) => (
				<NavItemRow key={item.label} item={item} onNavigate={onNavigate} />
			))}
		</nav>
	);
}
